<script setup>
  import { ref, computed, onMounted, onErrorCaptured, watchEffect } from 'vue';
  import { useProForecastStore } from '@/stores/ProForecastStore';
  import { useUserStore } from '@/stores/UserStore';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import ProForecastDaily from '@/components/ProForecastDaily.vue';
  import ProForecastExtended from '@/components/ProForecastExtended.vue';
  import ProForecastOutOfSeasonBanner from '@/components/ProForecastOutOfSeasonBanner.vue';
  import ProForecastUpgradeContent from '@/components/ProForecastUpgradeContent.vue';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import useConfigurationGlobals from '@/composables/useConfigurationGlobals';
  import DataContainerLoader from '@/components/DataContainerLoader.vue'; 
  import WebViewVersionNumber from '@/components/WebViewVersionNumber.vue';
  import ProForecastNotAvailable from '@/components/ProForecastNotAvailable.vue';

  const props = defineProps({
    useNearestDailyForecast: {
      type: Boolean,
      default: false
    },
    completeReportView: {
      type: Boolean,
      default: false
    },
    storeId: {
      type: Number,
      default: null
    },
    isSample: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false
    }
  })

  const { $WEB_VIEW, $PLATFORM, $FEATURE_VERSION = 0 } = useSettingsGlobals();
  const { $BRAND } = useConfigurationGlobals();

  const userStore = useUserStore();
  const proForecastStore = useProForecastStore(props.storeId);
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const forecastType = ref(proForecastStore.proForecastData?.requested_forecast_type);
  const loadFooterAd = ref(false);
  const error = ref(null);

  const showUpgradeBlock = computed(() => !error.value && userStore.memberLevel < 49);
  const showOutOfSeasonBanner = computed(() => !error.value && !props.isSample && proForecastStore.isDailyOutOfSeason);
  const showOutOfSeasonBlock = computed(() => !error.value && props.isExtendedOutOfSeason && forecastType.value !== 'daily');

  const showDailyForecast = computed(() => 
    !error.value && 
    proForecastStore.spotForecastStateLoading && 
    (
      forecastType.value === 'daily' &&
      proForecastStore.proForecastDaily || 
      props.useNearestDailyForecast && 
      proForecastStore.proForecastDailyNearest
    )
  );

  const showExtendedForecast = computed(() => 
    !error.value &&
    !proForecastStore.isExtendedOutOfSeason && 
    forecastType.value === 'extended' || 
    proForecastStore.hasExtendedProForecast && 
    !proForecastStore.isExtendedOutOfSeason
  );

  const showExtenededNotAvailable = computed(() => !error.value && showExtendedForecast.value && !proForecastStore.isExtendedPublished);
  const dailyForecast = computed(() => props.useNearestDailyForecast ? proForecastStore.proForecastDailyNearest : proForecastStore.proForecastDaily);
  const platformParamAvailable = computed(() => !$WEB_VIEW || $WEB_VIEW && $FEATURE_VERSION >= 1.0);
  const showVersionNumber = computed(() => !props.completeReportView);

  const dailyForecaster = computed(() => dailyForecast.value ? proForecastStore.getForecaster(dailyForecast.value.forecaster_member_id) : null);
  const extendedForecaster = computed(() => proForecastStore.proForecastExtended ? proForecastStore.getForecaster(proForecastStore.proForecastExtended.forecaster_member_id) : null);
  
  const setPageDatasets = () => {
    const body = document.documentElement;

    if ($WEB_VIEW) {
      body.dataset.webView = true;
      body.dataset.brand = $BRAND.toLowerCase();

      if (platformParamAvailable.value) {
        body.dataset.platform = $PLATFORM.toLowerCase();
      }
    }
  }

  const handleAccordianToggle = (e) => {
    const { collapsed, spotId } = e;

    const spotState = {
      ...proForecastStore.spotForecastState,
      [spotId]: {
        ...proForecastStore.spotForecastState[spotId],
        collapsed: collapsed
      }
    }

    proForecastStore.spotForecastState = spotState;
  }

  const handleStateChange = (newState) => {
    proForecastStore.spotForecastState = newState;
  }

  const handleSettingsChange = (newSettings) => {
    proForecastStore.spotForecastSettings = newSettings;
  }

  onMounted(() => {
    setPageDatasets();

    loadFooterAd.value = true;

    if (!$WEB_VIEW) {
      googleAnalyticsStore.sendEvent("Pro_Forecast", {
        "Forecast_Name": proForecastStore.proForecastName,
        "Forecast_Type": forecastType.value === 'daily' ? 'Daily' : 'Extended',
        "Complete_Report_View": props.completeReportView,
      });
    }
  });
  
  watchEffect(() => {
    if (!proForecastStore.recentlyViewedProForecastIdsLoading) return;

    if (proForecastStore.proForecastId && !proForecastStore.recentlyViewedProForecastIds.includes(proForecastStore.proForecastId)) {
      proForecastStore.recentlyViewedProForecastIds = [proForecastStore.proForecastId, ...proForecastStore.recentlyViewedProForecastIds]

      if (proForecastStore.recentlyViewedProForecastIds.length > 5) {
        proForecastStore.recentlyViewedProForecastIds.pop();
      }
    }
  }, { flush: 'post'})

  onErrorCaptured((e) => {
    console.warn(e);

    error.value = { 
      title: 'There was a problem loading the Pro Forecast',
      message: `Please try again later.`,
      errorText: e.message
    }

    console.warn('There was an error retrieving the Pro Forecast.', e);

    return false;
  })  
</script>

<template>   
  <div class="jwx-pro-forecast-content">
    <DataContainerLoader v-if="loading" />
    <div
      v-if="!loading && !error"
      class="jwx-pro-forecast jwx-page"
      :data-center-content="showOutOfSeasonBlock"
      :data-sample="proForecastStore.isSampleForecast ? true : null"
    >
      <header v-if="!$WEB_VIEW" class="jwx-toolbar-header">
        <div class="jwx-toolbar-header__main-content">
          <div class="jwx-toolbar-header__title">
            <h2 class="jwx-toolbar-header__title-text">{{ proForecastStore.proForecastName }}</h2>
          </div>
          <slot name="toolbarActions"></slot>
        </div>
      </header>
      <div style="position: relative; margin-block-start: 2.5rem; z-index: 1;">
        <ProForecastUpgradeContent v-if="showUpgradeBlock" v-once />
        <ProForecastOutOfSeasonBanner v-else-if="showOutOfSeasonBanner" v-once />
      </div>
      <ProForecastDaily
        v-if="showDailyForecast"
        :proForecast="dailyForecast"
        :hasExtendedForecast="proForecastStore.hasExtendedProForecast"
        :forecaster="dailyForecaster"
        :showDailyForecast="proForecastStore.showDailyForecast"
        :spotForecastState="proForecastStore.spotForecastState"
        :spotForecastStateLoading="proForecastStore.spotForecastStateLoading"
        :spotForecastSettings="proForecastStore.spotForecastSettings"
        @accordian:toggle="handleAccordianToggle"
        @state:change="handleStateChange"
        @settings:change="handleSettingsChange"
      />

      <ProForecastExtended 
        v-if="showExtendedForecast && !showExtenededNotAvailable"
        :proForecast="proForecastStore.proForecastExtended"
        :requestedForecastType="proForecastStore.requestedForecastType"
        :forecaster="extendedForecaster"
      />
      <ProForecastNotAvailable v-else-if="showExtenededNotAvailable" v-once />
      
      <WebViewVersionNumber v-if="showVersionNumber" class="jwx-page__version" style="background: var(--jwx-surface-level-1); padding-right: .775rem;" />
    </div>
  </div>
</template>
